import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { NespressoOffer, getDiscountedPrice } from '../config/offers';

// Nespresso brand colors
const NESPRESSO = {
  brown: '#986f38',
  brownHover: '#815e2f',
  gold: '#c69f36',
  black: '#000000',
  darkGray: '#292929',
  lightGray: '#f9f9f9',
  border: '#e6e5e4',
};

interface OfferCardProps {
  offer: NespressoOffer;
  isSelected: boolean;
  onSelect: (offerId: string) => void;
  onAccept: (offerId: string) => void;
}

const OfferCard: React.FC<OfferCardProps> = ({
  offer,
  isSelected,
  onSelect,
  onAccept,
}) => {
  const [imageError, setImageError] = useState(false);
  const discountedPrice = getDiscountedPrice(offer);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      onClick={() => onSelect(offer.id)}
      className={`
        relative rounded-lg p-6 cursor-pointer transition-all duration-300 border-2
        ${isSelected
          ? 'bg-white'
          : 'bg-white/5 hover:bg-white/10 border-transparent hover:border-white/20'
        }
      `}
      style={{
        borderColor: isSelected ? NESPRESSO.gold : undefined,
      }}
    >
      {/* Tag */}
      {offer.tag && (
        <span
          className={`absolute top-4 right-4 text-xs px-3 py-1 rounded font-medium
            ${isSelected ? 'text-white' : 'bg-white/10 text-white/60'}`}
          style={{
            backgroundColor: isSelected ? NESPRESSO.brown : undefined,
          }}
        >
          {offer.tag}
        </span>
      )}

      {/* Product Image */}
      <div
        className={`w-full aspect-square rounded-lg mb-4 flex items-center justify-center overflow-hidden
          ${isSelected ? 'bg-gray-100' : 'bg-white/5'}`}
      >
        {!imageError ? (
          <img
            src={offer.image}
            alt={offer.name}
            className="w-full h-full object-contain p-4"
            onError={() => setImageError(true)}
          />
        ) : (
          <span
            className={`text-sm text-center px-2 ${
              isSelected ? 'text-gray-400' : 'text-white/40'
            }`}
          >
            {offer.name}
          </span>
        )}
      </div>

      {/* Product Info */}
      <h3
        className={`font-medium text-lg mb-1 ${isSelected ? '' : 'text-white'}`}
        style={{
          color: isSelected ? NESPRESSO.black : undefined,
        }}
      >
        {offer.name}
      </h3>
      <p
        className={`text-sm mb-3 font-light ${
          isSelected ? 'text-gray-500' : 'text-white/60'
        }`}
      >
        {offer.description}
      </p>

      {/* Pricing */}
      <div className="flex items-center gap-2 mb-4">
        <span
          className={`text-lg font-medium ${isSelected ? '' : 'text-white'}`}
          style={{
            color: isSelected ? NESPRESSO.black : undefined,
          }}
        >
          ${discountedPrice.toFixed(2)}
        </span>
        <span
          className={`text-sm line-through ${
            isSelected ? 'text-gray-400' : 'text-white/40'
          }`}
        >
          ${offer.originalPrice.toFixed(2)}
        </span>
        <span
          className="text-xs font-medium px-2 py-0.5 rounded"
          style={{
            backgroundColor: isSelected ? NESPRESSO.gold : 'rgba(198, 159, 54, 0.2)',
            color: isSelected ? 'white' : NESPRESSO.gold,
          }}
        >
          -{offer.discountPercent}%
        </span>
      </div>

      {/* Accept Button (only shown when selected) */}
      {isSelected && (
        <motion.button
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          onClick={(e) => {
            e.stopPropagation();
            onAccept(offer.id);
          }}
          className="w-full py-3 text-white font-medium transition-colors"
          style={{
            backgroundColor: NESPRESSO.brown,
            borderRadius: '3px',
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.backgroundColor = NESPRESSO.brownHover)
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.backgroundColor = NESPRESSO.brown)
          }
        >
          Accept Offer
        </motion.button>
      )}

      {/* Selection indicator */}
      {isSelected && (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="absolute -top-2 -right-2 w-8 h-8 rounded-full flex items-center justify-center"
          style={{ backgroundColor: NESPRESSO.gold }}
        >
          <svg
            className="w-5 h-5 text-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={3}
              d="M5 13l4 4L19 7"
            />
          </svg>
        </motion.div>
      )}
    </motion.div>
  );
};

export default OfferCard;
